import { useEffect, useState } from 'react';

import { Filter, Plus, Trash, X } from 'lucide-react';

import { Button } from '@/components/TailwindComponents/base/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/TailwindComponents/base/Popover/Popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/TailwindComponents/base/Select/Select';
import { DatePicker } from '@/components/TailwindComponents/components/Form/DatePicker/DatePicker';
import { DateTimePicker } from '@/components/TailwindComponents/components/Form/DateTimePicker/DateTimePicker';

import { TailwindStyles } from '../../../styles';
import { displayDateTimeFormat } from '../../../utils/displayDateTimeFormat';
import { DebouncedInput } from '../../Form/DebouncedInput/DebouncedInput';
import { tableFilterBooleanOperators } from '../constants/filterOperators';
import {
  FilterRow,
  TableFilterInputType,
  TableFilterItem,
  TableGridFilterModel,
  TableGridLogicOperator,
} from '../types';
import { filterValuesAreValid } from '../utils/filterValidators';

interface DynamicFilterMenuProps {
  filterOptions: Array<TableFilterItem>;
  onChange?: (filter: TableGridFilterModel) => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const DynamicFilterMenu = ({
  filterOptions,
  onChange,
  isOpen = false,
  onOpenChange,
}: DynamicFilterMenuProps) => {
  const [isOpenState, setIsOpenState] = useState(isOpen);
  const [logicalOperator, setLogicalOperator] =
    useState<TableGridLogicOperator>(TableGridLogicOperator.And);
  const [filters, setFilters] = useState<FilterRow[]>([]);

  const mapFilterRowsToModel = () => {
    const model: TableGridFilterModel = {
      items: [],
      logicOperator: logicalOperator,
    };

    // format the date value to ISO string
    const formattedValue = (filter: FilterRow) => {
      if (filter.inputType === 'date') {
        return displayDateTimeFormat(new Date(filter.value));
      }
      if (filter.inputType === 'datetime') {
        return displayDateTimeFormat(
          new Date(filter.value),
          'MM/dd/yyyy HH:mm',
        );
      }
      return filter.value;
    };

    filters.forEach((filter) => {
      model.items.push({
        field: filter.column,
        operator: filter.operator,
        value: formattedValue(filter),
        id: filter.id,
      });
    });
    model.logicOperator = logicalOperator;
    return model;
  };

  useEffect(() => {
    const data = mapFilterRowsToModel();
    if (data.items.length > 0 && filterValuesAreValid(filters)) {
      onChange?.(data as TableGridFilterModel);
    }
  }, [filters]);

  const handleOnOpenChange = (open: boolean) => {
    setIsOpenState(open);
    onOpenChange?.(open);
  };

  const allColumns = filterOptions.map((filter) => {
    return {
      value: filter.field,
      label: filter.fieldName,
    };
  });

  const initalizeFirstFilter = () => {
    // find the fitst filter options
    const firstFilterOption = filterOptions[0];
    const newFilter = {
      id: Date.now().toString(),
      column: firstFilterOption.field,
      operator: firstFilterOption.operators[0].value,
      value: '',
      conjunction: logicalOperator,
      inputType: firstFilterOption.inputType,
    };
    setFilters([newFilter as FilterRow]);
  };

  const addEmphyFilter = () => {
    // get the first filter option that is not used in the filters array
    const usedColumns = filters.map((filter) => filter.column);
    const firstFilterOption = filterOptions.find(
      (option) => !usedColumns.includes(option.field),
    );
    if (!firstFilterOption) {
      return;
    }
    const newFilter = {
      id: Date.now().toString(),
      column: firstFilterOption.field,
      operator: firstFilterOption.operators[0].value,
      value: '',
      conjunction: logicalOperator,
      inputType: firstFilterOption.inputType,
    };
    setFilters([...filters, newFilter as FilterRow]);
  };

  const removeFilter = (id: string) => {
    const newFilters = filters.filter((filter) => filter.id !== id);

    // if it's the last filter, remove all filters
    if (newFilters.length === 0) {
      removeAll();
      return;
    }

    setFilters(newFilters);
  };

  const removeAll = () => {
    setFilters([]);
    handleOnOpenChange(false);
    onChange?.({
      items: [],
    });
  };

  const updateFilter = (id: string, field: keyof FilterRow, value: string) => {
    const filter = filters.find((f: FilterRow) => f.id === id);

    if (!filter || filter?.[field] === value) {
      return;
    }

    // for specific column, set the input type
    if (filter && field === 'column') {
      filter.inputType = filterOptions.find((option) => option.field === value)
        ?.inputType as TableFilterInputType;
    }
    setFilters(
      filters.map((f: FilterRow) =>
        f.id === id ? { ...f, [field]: value } : f,
      ),
    );
  };

  const updateFilterOperator = (id: string, operator: string) => {
    setFilters(
      filters.map((filter) =>
        filter.id === id ? { ...filter, operator } : filter,
      ),
    );
  };

  const activeFilters = filters.filter((f) => f.value).length;

  const getAvailableColumns = (currentId: string) => {
    const selectedColumns = new Set(filters.map((f) => f.column));
    return allColumns.filter(
      (column) =>
        !selectedColumns.has(column.value) ||
        filters.find((f) => f.id === currentId)?.column === column.value,
    );
  };

  const renderValueInput = (filter: FilterRow) => {
    switch (filter.inputType) {
      case 'date':
        return (
          <div className="min-w-[200px]">
            <DatePicker
              onSelect={(date) => {
                updateFilter(
                  filter.id,
                  'value',
                  date ? displayDateTimeFormat(date, 'MM/dd/yyyy HH:mm') : '',
                );
              }}
            />
          </div>
        );
      case 'datetime':
        return (
          <div className="min-w-[200px]">
            <DateTimePicker
              onSelect={(date) => {
                updateFilter(
                  filter.id,
                  'value',
                  date ? displayDateTimeFormat(date, 'MM/dd/yyyy HH:mm') : '',
                );
              }}
            />
          </div>
        );
      case 'boolean':
        return (
          <div className="min-w-[200px]">
            <Select
              value={filter.value}
              onValueChange={(value) => updateFilter(filter.id, 'value', value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select supplier" />
              </SelectTrigger>
              <SelectContent>
                {tableFilterBooleanOperators.map((operator) => (
                  <SelectItem key={operator.value} value={operator.value}>
                    {operator.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      case 'number':
        return (
          <div className="min-w-[200px]">
            <DebouncedInput
              className="w-full"
              placeholder="Filter value"
              value={filter.value}
              type="number"
              onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
            />
          </div>
        );
      case 'list':
        return (
          <div className="min-w-[200px]">
            <Select
              value={filter.value}
              onValueChange={(value) => updateFilter(filter.id, 'value', value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select value" />
              </SelectTrigger>
              <SelectContent>
                {filterOptions
                  .find((option) => option.field === filter.column)
                  ?.inputOptions?.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        );
      default:
        return (
          <div className="min-w-[200px]">
            <DebouncedInput
              className="w-full"
              placeholder="Filter value"
              value={filter.value}
              onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
            />
          </div>
        );
    }
  };

  const renderOperatorOptions = (filter: FilterRow) => {
    const operators = filterOptions.find(
      (option) => option.field === filter.column,
    )?.operators;

    return (
      <Select
        value={filter.operator}
        onValueChange={(value) => updateFilterOperator(filter.id, value)}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {operators?.map((operator) => (
            <SelectItem key={operator.value} value={operator.value}>
              {operator.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  const handleOnPopoverOpenChange = (open: boolean) => {
    // Add filter so user can instantly select a column
    if (filters.length === 0) {
      initalizeFirstFilter();
    }
    handleOnOpenChange(open);
  };

  const handleOnLogicalOperatorChange = (value: TableGridLogicOperator) => {
    setLogicalOperator(value);
    setFilters(filters.map((filter) => ({ ...filter, conjunction: value })));
  };

  return (
    <Popover open={isOpenState} onOpenChange={handleOnPopoverOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size={activeFilters === 0 ? 'icon' : 'default'}
        >
          <Filter className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[800px] p-0" align="start">
        <div className="p-4 space-y-2">
          <div className="grid grid-cols-[auto_100px_1fr_1fr_200px] gap-2 items-center font-medium text-sm text-muted-foreground">
            <div className="w-10"></div>
            <div></div>
            <div>Columns</div>
            <div>Operator</div>
            <div>Value</div>
          </div>
          {filters.map((filter, index) => (
            <div
              key={filter.id}
              className="grid grid-cols-[auto_100px_1fr_1fr_200px] gap-2 items-center"
            >
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeFilter(filter.id)}
                className="w-10"
              >
                <X className="h-4 w-4" />
              </Button>
              <div>
                {index > 0 ? (
                  <Select
                    disabled={index > 1}
                    value={logicalOperator}
                    onValueChange={(value) =>
                      handleOnLogicalOperatorChange(
                        value as TableGridLogicOperator,
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(TableGridLogicOperator).map((operator) => (
                        <SelectItem key={operator} value={operator}>
                          {operator.charAt(0).toUpperCase() + operator.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="h-10"></div>
                )}
              </div>
              <Select
                value={filter.column}
                onValueChange={(value) =>
                  updateFilter(filter.id, 'column', value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select column" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableColumns(filter.id).map((column) => (
                    <SelectItem key={column.value} value={column.value}>
                      {column.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {renderOperatorOptions(filter)}
              {renderValueInput(filter)}
            </div>
          ))}
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              className="text-primary"
              style={{
                color: TailwindStyles.colors.Red.primary,
              }}
              onClick={addEmphyFilter}
            >
              <Plus className="mr-2 h-4 w-4" />
              ADD FILTER
            </Button>
            {filters.length > 0 && (
              <Button
                variant="ghost"
                className="text-primary"
                style={{
                  color: TailwindStyles.colors.Red.primary,
                }}
                onClick={removeAll}
              >
                <Trash className="mr-2 h-4 w-4" />
                REMOVE ALL
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

DynamicFilterMenu.displayName = 'DynamicFilterMenu';
